import {
  As, Badge, Box, Button, ButtonProps, HStack, Icon, Link, Text, Tooltip, useColorModeValue,
} from "@chakra-ui/react";
import RoundBadge from "components/Badge/RoundBadge";
import { GrWorkshop } from "react-icons/gr";
import { Link as RouterLink } from "react-router";
import { useAnalytics } from "services/analytics";

export interface BadgeProps {
  value: true | number;
  color?: string;
}

interface NavButtonProps extends ButtonProps {
  icon: As;
  label: string;
  route?: string;
  subtle?: boolean;
  expanded?: boolean;
  badge?: BadgeProps;
  isActive: boolean;
}

function NavBadge({
  value, color
}: BadgeProps) {
  if (typeof value === 'number') {
    if (color === undefined) {
      return (
        <Text size="xs">
          {`(${value})`}
        </Text>
      )
    }
    return (
      <Badge variant="solid" colorScheme={color} borderRadius="8px" size="xs">
        {value}
      </Badge>
    )
  }
    return (
      <Box alignSelf="stretch" ml="1">
        <RoundBadge variant="solid" alignSelf="center" colorScheme={color} />
      </Box>
    );

}

export default function SidebarNavButton({
  route,
  icon,
  label,
  subtle = false,
  expanded = true,
  badge,
  isActive,
  ...buttonProps
}: NavButtonProps) {
  const activeBgColor = useColorModeValue("gray.100", "gray.700");
  const { trackEvent } = useAnalytics();

  const handleClick = () => {
    trackEvent("sidebar_item.click", {
      item_name: label,
      destination: route,
      badge,
    });
  };

  const isExternal = !!route && /^https?:\/\//i.test(route);

  const linkProps = isExternal
    ? { href: route, target: "_blank", rel: "noopener noreferrer" }
    : { as: RouterLink, to: route || "#" };

  return (
    <Link
      {...linkProps}
      style={{ textDecoration: "none" }}
      _hover={{ textDecoration: "none" }}
      _focus={{ boxShadow: "none" }}
      onClick={handleClick}
    >
      <Tooltip label={label} isDisabled={expanded} placement="right">
        <Button
          width="100%"
          variant="tertiary"
          justifyContent="start"
          size="xs"
          bg={isActive ? activeBgColor : undefined}
          rightIcon={(expanded && badge) ? <NavBadge {...badge} /> : undefined}
          {...buttonProps}
        >
          <HStack gap={expanded ? "3" : "0"} transition="gap 0.6s ease-in-out">
            {[GrWorkshop].includes(icon) ? (
              <Icon as={icon} boxSize="5" sx={{ path: { stroke: "fg.subtle" } }} />
            ) : (
              <Icon as={icon} boxSize="5" color="fg.subtle" />
            )}
            <Text
              color={subtle ? "gray.400" : undefined}
              maxWidth={expanded ? "150px" : "0px"}
              overflow="hidden"
              whiteSpace="nowrap"
              opacity={expanded ? 1 : 0}
              transition="opacity 0.6s ease-in-out, max-width 0.6s ease-in-out"
            >
              {label}
            </Text>
          </HStack>
        </Button>
      </Tooltip>
    </Link>
  );
}
